import type { paths } from '@/lib/api-types-expenses';
import { isClassFilterValue } from '../internal-class';
import type { DocumentStatus, ExpenseStatus, Payer } from './expense-labels';

export type ListQuery = NonNullable<paths['/v1/expenses']['get']['parameters']['query']>;

/** The URL parameters the list keeps its filters in (decision 101), besides the search `q`. */
export const FILTER_KEYS = [
  'period',
  'category',
  'payer',
  'document',
  'status',
  'owner',
  'inReport',
  // #915 C4: the internal class, `none` for «Nessuna»; finance ignores it for the owner.
  'class',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/** «Tutti i periodi»: the value the period takes once its label is removed. */
export const ALL_PERIODS = 'all';

/** The month a date falls in, as the period filter writes it: «2026-10». */
export function monthOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Recent months up to `today`'s, newest first: the communications log's period choices. */
export function recentMonths(today: Date, count = 12): string[] {
  const out: string[] = [];
  for (let back = 0; back < count; back += 1) {
    out.push(monthOf(new Date(today.getFullYear(), today.getMonth() - back, 1)));
  }
  return out;
}

/** «2026-10» → «ottobre 2026» in the reader's language. */
export function monthLabel(month: string, language: string | undefined): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (year === undefined || monthNumber === undefined || Number.isNaN(year + monthNumber)) {
    return month;
  }
  return new Intl.DateTimeFormat(language ?? 'it', { month: 'long', year: 'numeric' }).format(
    new Date(year, monthNumber - 1, 1),
  );
}

/** The first and last day of a «2026-10» month, as the API's `from` and `to`. */
export function monthRange(month: string): { from: string; to: string } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return null;
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  if (monthNumber < 1 || monthNumber > 12) return null;
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return { from: `${match[1]!}-${match[2]!}-01`, to: `${match[1]!}-${match[2]!}-${lastDay}` };
}

/**
 * The filters as `GET /v1/expenses` reads them. `period` is the month in force (the current one
 * when the address names none, «all» for every period); the owner is sent only when the caller may
 * filter by it, so a submitter's stale link never earns a 403.
 */
export function buildListQuery(
  filters: Readonly<Partial<Record<FilterKey, string | null>>> & { readonly q: string },
  options: {
    readonly canFilterByOwner: boolean;
    readonly offset?: number;
    readonly sort?: ListQuery['sort'];
    readonly direction?: ListQuery['direction'];
    readonly cursor?: string | undefined;
    /** Rows per page (10, 25 or 50), sent as `limit`. */
    readonly pageSize?: number | undefined;
  },
): ListQuery {
  const query: ListQuery = {};
  const range = filters.period == null ? null : monthRange(filters.period);
  if (range !== null) {
    query.from = range.from;
    query.to = range.to;
  }
  if (filters.category) query.category = [filters.category];
  if (filters.payer) query.payer = [filters.payer as Payer];
  if (filters.document) query.documentStatus = [filters.document as DocumentStatus];
  if (filters.status) query.status = [filters.status as ExpenseStatus];
  if (filters.q !== '') query.supplier = filters.q;
  // Release 1's generated query does not know #915's `class`.
  if (filters.class && isClassFilterValue(filters.class)) {
    (query as ListQuery & { class?: string[] }).class = [filters.class];
  }
  if (filters.owner && options.canFilterByOwner) query.owner = filters.owner;
  if (filters.inReport === 'false') query.inReport = 'false';
  if (options.offset !== undefined) query.offset = options.offset;
  if (options.sort !== undefined) query.sort = options.sort;
  if (options.direction !== undefined) query.direction = options.direction;
  if (options.cursor !== undefined) query.cursor = options.cursor;
  if (options.pageSize !== undefined) query.limit = options.pageSize;
  return query;
}
